import { t } from '@shared/i18n';
import { type FormEvent, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import { configError, supabase } from '../../supabase';

export default function Login() {
  const { session, signIn, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // « mot de passe oublié » : lien par e-mail vers /nouveau-mot-de-passe
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(false);

  if (!loading && session) return <Navigate to="/organisateur" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (forgot) {
        const { error: err } = await supabase!.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: `${window.location.origin}/nouveau-mot-de-passe`,
        });
        if (err) throw err;
        setSent(true);
        return;
      }
      await signIn(email, password);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container" style={{ maxWidth: 420, paddingTop: 56 }}>
      <form className="card stack" onSubmit={submit}>
        <div style={{ textAlign: 'center' }}>
          <span className="logo big" style={{ width: 64, height: 64, fontSize: '1.6rem', borderRadius: 18 }}>
            FM
          </span>
          <h2 style={{ marginTop: 16 }}>{t('Espace organisateur', 'Organiser area')}</h2>
          <p className="muted small">
            {t(
              'Connecte-toi avec ton compte FollowMe (staff ou administrateur).',
              'Sign in with your FollowMe account (staff or administrator).',
            )}
          </p>
        </div>
        {!supabase && (
          <div className="notice">
            {t(
              "Le site n'est pas encore relié à la base de données.",
              'The website is not connected to the database yet.',
            )}{' '}
            {configError()}
          </div>
        )}
        <label className="field">
          {t('E-mail', 'E-mail')}
          <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        {!forgot && (
          <label className="field">
            {t('Mot de passe', 'Password')}
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
        )}
        {error && <div className="notice">{error}</div>}
        {sent && (
          <div className="notice ok">
            {t(
              'Si un compte existe avec cette adresse, un lien pour choisir un nouveau mot de passe vient d’être envoyé.',
              'If an account exists with this address, a link to choose a new password has just been sent.',
            )}
          </div>
        )}
        <button className="btn" disabled={busy || !supabase}>
          {busy
            ? forgot
              ? t('Envoi…', 'Sending…')
              : t('Connexion…', 'Signing in…')
            : forgot
              ? t('Recevoir le lien', 'Send me the link')
              : t('Se connecter', 'Sign in')}
        </button>
        <button
          type="button"
          className="btn secondary small"
          onClick={() => {
            setForgot((f) => !f);
            setSent(false);
            setError(null);
          }}
        >
          {forgot
            ? t('‹ Retour à la connexion', '‹ Back to sign in')
            : t('Mot de passe oublié ?', 'Forgot your password?')}
        </button>
      </form>
    </div>
  );
}
